import { useT } from '../context/LanguageContext';
import React, { useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize2 } from 'lucide-react';
import { mockData } from '../mock';
import { useContent } from '../context/ContentContext';
import { SiteFieldEditor } from './admin/SiteFieldEditor';

const VideoPromo = () => {
  const { t } = useT();
  const { content } = useContent();
  const site = content.site || {};
  const videoRef = useRef(null);
  const boxRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  const goFullscreen = () => {
    const video = videoRef.current;
    const box = boxRef.current;
    if (!video) return;
    if (box?.requestFullscreen) {
      box.requestFullscreen().catch(() => video.webkitEnterFullscreen?.());
    } else if (video.requestFullscreen) {
      video.requestFullscreen().catch(() => {});
    } else if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
    } else if (box?.webkitRequestFullscreen) {
      box.webkitRequestFullscreen();
    }
  };

  return (
    <section id="promo" className="relative bg-black py-20 md:py-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p data-edit-fields="promoEyebrow" className="cursor-pointer text-[#ff2e2e] font-bold uppercase tracking-widest mb-3 text-sm">{t(site.promoEyebrow || 'Trailer 2027')}</p>
          <h2
            data-edit-fields="promoHeading"
            className="cursor-pointer text-5xl md:text-7xl font-black text-white tracking-tighter"
            style={{ fontFamily: "'Oktah Round', sans-serif" }}
          >
            {t(site.promoHeading || 'Sjå kva som ventar deg')}
          </h2>
          <div className="mt-5 flex justify-center">
            <SiteFieldEditor title="Rediger trailer" fieldKeys={['promoEyebrow', 'promoHeading', 'promoVideo', 'promoPoster']} testId="edit-site-promo-button" />
          </div>
        </div>

        <div className="relative max-w-5xl mx-auto rounded-3xl overflow-hidden bg-[#0a0710] border border-white/10 shadow-2xl shadow-[#ff2e2e]/10">
          <div ref={boxRef} data-edit-fields="promoVideo,promoPoster" className="relative aspect-video group bg-black [&:fullscreen]:flex [&:fullscreen]:items-center [&:fullscreen]:justify-center">
            <video
              ref={videoRef}
              className="w-full h-full object-cover [:fullscreen_&]:object-contain"
              playsInline
              muted={muted}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              onClick={togglePlay}
              poster={site.promoPoster || mockData.promoPoster}
              preload="metadata"
              data-testid="promo-video"
            >
              <source src={site.promoVideo || mockData.promoVideo} type="video/mp4" />
              Nettlesaren din stør ikkje videoformatet.
            </video>

            {/* Center Play Button (visible when paused) */}
            {!playing && (
              <button
                onClick={togglePlay}
                className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors"
                aria-label="play video"
              >
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#ff2e2e] flex items-center justify-center shadow-2xl shadow-[#ff2e2e]/50 group-hover:scale-110 transition-transform">
                  <Play size={40} className="text-black ml-1.5" strokeWidth={2.5} fill="black" />
                </div>
              </button>
            )}

            {/* Controls Bar */}
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 bg-gradient-to-t from-black/90 to-transparent flex items-center gap-3">
              <button
                onClick={togglePlay}
                className="w-11 h-11 rounded-full bg-[#ff2e2e] text-black flex items-center justify-center hover:bg-[#ff4d4d] transition-colors"
                aria-label={playing ? 'pause' : 'play'}
              >
                {playing ? <Pause size={18} fill="black" /> : <Play size={18} className="ml-0.5" fill="black" />}
              </button>
              <button
                onClick={toggleMute}
                className="w-11 h-11 rounded-full border border-white/20 text-white hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label={muted ? 'unmute' : 'mute'}
              >
                {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <div className="flex-1" />
              <span className="text-white/70 text-sm font-semibold hidden sm:block">RW Promo 2027</span>
              <button
                onClick={goFullscreen}
                data-testid="promo-video-fullscreen-button"
                className="w-11 h-11 rounded-full border border-white/20 text-white hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label="fullscreen"
              >
                <Maximize2 size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoPromo;
